import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { decode } from 'he';
import {
  GET_TOPICS_PAGE,
  GET_MEDIA_ASSETS,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_PAGES,
  GET_PUBLIC_SITE,
  GET_TINYCMS_DELETED_ARTICLES,
  RESTORE_TINYCMS_ARTICLE,
} from '../../graphql';
import { RecentlyDeleted, type DeletedItem } from '../cms/RecentlyDeleted';
import { CmsPagesView } from '../cms/CmsPages';
import { CmsFormsView } from '../cms/CmsForms';
import { AgentNewPanel } from '../cms/AgentNewPanel';
import { CmsSite } from '../cms/CmsSite';
import { primaryCategory } from '../cms/categories';
import { CreateTopicModal } from '../topics/CreateTopicModal';
import { useAccount } from '../../context/AccountContext';
import { useAddons } from '../cms/addons';
import { MediaLibrary } from './MediaLibrary';
import { Loader } from '../common/PageLoader';

type LibraryFilter = 'all' | 'review' | 'draft' | 'scheduled' | 'live';

/** Category chips shown before "more": the most used ones. */
const TOP_CATEGORIES = 12;
type ContentView = 'pages' | 'articles' | 'forms' | 'media' | 'design';

interface ContentTopic {
  id: string;
  title?: string | null;
  summary?: string | null;
  publishedDate?: string | null;
  publishStatus?: string | null;
  contentType?: 'webpage' | 'video' | 'image' | 'article' | null;
  category?: string | null;
  atomizedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  verifiedFacts?: unknown[] | null;
  dataSource?: { id: string; name: string; type: string } | null;
}

/**
 * One status per record, so the chip counts add up to All.
 * Needs review is published work that has not been atomized yet — only when
 * the site has Atomize switched on. Without it nothing can atomize the article,
 * so published is simply Live.
 */
function libraryFilter(topic: ContentTopic, atomize = true): Exclude<LibraryFilter, 'all'> {
  const status = topic.publishStatus || 'draft';
  if (status === 'scheduled') return 'scheduled';
  if (status === 'draft') return 'draft';
  if (atomize && !topic.atomizedAt) return 'review';
  return 'live';
}

function statusLabel(topic: ContentTopic, atomize: boolean) {
  const bucket = libraryFilter(topic, atomize);
  if (bucket === 'review') return 'Needs review';
  if (bucket === 'scheduled') return 'Scheduled';
  if (bucket === 'draft') return 'Draft';
  return 'Live';
}

interface GetTopicsPageResp {
  topicsPage: {
    items: ContentTopic[];
    total: number;
    offset: number;
    limit: number;
    counts: Record<LibraryFilter, number>;
    categories: Array<{ name: string; count: number }>;
  };
}

/** Articles per page of the Content list. */
const PAGE_SIZE = 50;

const STATUS_STYLE: Record<Exclude<LibraryFilter, 'all'>, { dot: string; text: string }> = {
  live: { dot: 'var(--ink-green)', text: 'var(--ink-ink)' },
  review: { dot: 'var(--ink-amber)', text: 'var(--ink-amber)' },
  draft: { dot: 'var(--ink-ink3)', text: 'var(--ink-ink2)' },
  scheduled: { dot: 'var(--ink-accent)', text: 'var(--ink-ink)' },
};

/** Short codes are stored lowercase. The library shows them as NCAAF, MLB. */
function topicLabel(value?: string | null) {
  const raw = primaryCategory(value);
  if (!raw) return '—';
  if (/^[a-z0-9]{1,8}$/i.test(raw)) return raw.toUpperCase();
  return raw;
}

function stamp(topic: ContentTopic) {
  return topic.updatedAt || topic.publishedDate || topic.createdAt || null;
}

function clock(iso: string) {
  const d = new Date(iso);
  return d.getMinutes() === 0 ? format(d, 'h a') : format(d, 'h:mm a');
}

function rowTime(topic: ContentTopic) {
  const iso = stamp(topic);
  if (!iso) return '—';
  const time = clock(iso);
  if (libraryFilter(topic) === 'scheduled') return `${format(new Date(iso), 'EEE')} ${time}`;
  return time;
}

function dayBucket(topic: ContentTopic) {
  if (libraryFilter(topic) === 'scheduled')
    return { key: 'scheduled', label: 'SCHEDULED', order: 0 };
  const iso = stamp(topic);
  if (!iso) return { key: 'earlier', label: 'EARLIER', order: 100000 };
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(new Date()) - start(d)) / 86400000);
  const dateLabel = format(d, 'MMM d').toUpperCase();
  if (diff === 0) return { key: 'today', label: `TODAY · ${dateLabel}`, order: 1 };
  if (diff === 1) return { key: 'yesterday', label: `YESTERDAY · ${dateLabel}`, order: 2 };
  return { key: `day-${start(d)}`, label: dateLabel, order: 3 + Math.max(diff, 0) };
}

/** Articles written in the same 10 minutes by the same source are one publish run. */
function batchKey(topic: ContentTopic) {
  const iso = topic.createdAt || topic.updatedAt || topic.publishedDate;
  if (!iso) return null;
  const bucket = Math.floor(new Date(iso).getTime() / 600000);
  return `${topic.dataSource?.id || 'manual'}|${bucket}`;
}

function markFor(name?: string | null) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'MC').slice(0, 2).toUpperCase();
}

function factLabel(topic: ContentTopic) {
  const n = topic.verifiedFacts?.length || 0;
  if (!n) return null;
  return n === 1 ? '1 fact' : `${n} facts`;
}

const FILTERS: Array<{ id: LibraryFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'review', label: 'Needs review' },
  { id: 'draft', label: 'Drafts' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'live', label: 'Live' },
];

export function ContentLayout() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [topicsOn, setTopicsOn] = useState<Set<string>>(new Set());
  const [allCategories, setAllCategories] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const uploadMedia = useRef<(() => void) | null>(null);
  const [openBatches, setOpenBatches] = useState<Record<string, boolean>>({});
  // Which page of the list (the server filters, counts and pages; PAGE_SIZE at a time).
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');

  const { accountId } = useAccount();
  // Facts and Needs review come from the Atomize add-on. Facts show while the
  // site holds it (even switched off); Needs review only while it is on.
  const { atomizeOn, atomizeHeld } = useAddons();
  const filters = atomizeOn ? FILTERS : FILTERS.filter((f) => f.id !== 'review');

  const { data: mediaData } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 1, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const mediaCount: number = mediaData?.mediaAssets?.total ?? 0;
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const pageCount: number = pagesData?.tinycmsPages?.length ?? 0;
  const { data: formsData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const formRows: Array<{ responses?: { unread?: number } }> = formsData?.tinycmsForms || [];
  const formCount = formRows.length;
  const formUnread = formRows.reduce((n, f) => n + (f.responses?.unread || 0), 0);

  // Public / Password / Offline, as Site settings sets it.
  const { data: publicData } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const publicAccount = publicData?.account;
  const visibility = !publicAccount
    ? null
    : !publicAccount.publicEnabled
      ? { label: 'Offline', dot: 'var(--ink-ink3)' }
      : publicAccount.publicAccessProtected
        ? { label: 'Password', dot: 'var(--ink-amber)' }
        : { label: 'Public', dot: 'var(--ink-green)' };

  // Search waits for a pause in typing, then asks the server.
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  // A new filter, category or search starts again at the first page.
  const categoryKey = [...topicsOn].sort().join('\u0000');
  useEffect(() => setOffset(0), [filter, categoryKey, query, atomizeOn]);

  const {
    data,
    previousData,
    loading,
    error: topicsError,
    refetch: refetchTopics,
  } = useQuery<GetTopicsPageResp>(GET_TOPICS_PAGE, {
    variables: {
      accountId,
      filter,
      categories: [...topicsOn],
      search: query || null,
      atomize: atomizeOn,
      offset,
      limit: PAGE_SIZE,
    },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Keep the current page on screen while the next one loads — but not after
  // it fails: then the error shows, never the previous results as if current.
  const page = (data ?? (loading ? previousData : undefined))?.topicsPage;
  const { data: deletedData, refetch: refetchDeleted } = useQuery(GET_TINYCMS_DELETED_ARTICLES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const deletedArticles: DeletedItem[] = deletedData?.tinycmsDeletedArticles || [];
  const apollo = useApolloClient();

  const topics = useMemo(() => page?.items || [], [page]);
  const total = page?.total ?? 0;

  // A tab picked (?view=) wins. Otherwise Pages, unless there are articles;
  // until the articles have loaded, Articles shows its loading state. (A
  // failed load counts as none.)
  const view: ContentView =
    viewParam === 'media' ||
    viewParam === 'pages' ||
    viewParam === 'articles' ||
    viewParam === 'forms' ||
    viewParam === 'design'
      ? viewParam
      : accountId && page && page.counts.all === 0
        ? 'pages'
        : 'articles';
  // Whole-account counts per status, from the server (not just this page).
  const counts = page?.counts ?? { all: 0, review: 0, draft: 0, scheduled: 0, live: 0 };
  // Every category in the account, most used first (so a feed that tags each
  // article many ways still leads with the categories that matter).
  const categories = useMemo(() => (page?.categories || []).map((c) => c.name), [page]);
  const shownCategories = allCategories
    ? categories
    : categories.filter((name, i) => i < TOP_CATEGORIES || topicsOn.has(name));

  const q = query.toLowerCase();
  // Already filtered, searched and in order (newest activity first) by the server.
  const items = topics;

  const rows = useMemo(() => {
    const byDay = new Map<string, { label: string; order: number; items: ContentTopic[] }>();
    for (const topic of items) {
      const day = dayBucket(topic);
      const group = byDay.get(day.key) || { label: day.label, order: day.order, items: [] };
      group.items.push(topic);
      byDay.set(day.key, group);
    }
    const out: Array<
      | { kind: 'day'; key: string; label: string }
      | { kind: 'batch'; key: string; label: string; sub: string; time: string; mark: string }
      | { kind: 'more'; key: string; batch: string; label: string }
      | { kind: 'article'; topic: ContentTopic; indent: boolean }
    > = [];
    const days = [...byDay.entries()].sort((a, b) => a[1].order - b[1].order);
    for (const [dayKey, day] of days) {
      out.push({ kind: 'day', key: dayKey, label: day.label });
      const seen = new Set<string>();
      for (const topic of day.items) {
        if (seen.has(topic.id)) continue;
        const key = batchKey(topic);
        const mates = key ? day.items.filter((t) => batchKey(t) === key) : [topic];
        if (key && mates.length >= 4) {
          mates.forEach((t) => seen.add(t.id));
          const open = Boolean(openBatches[key]) || Boolean(q);
          const source = topic.dataSource?.name || 'an agent';
          const cats = [...new Set(mates.map((t) => primaryCategory(t.category)).filter(Boolean))];
          out.push({
            kind: 'batch',
            key,
            label: `Batch of ${mates.length} published by ${source}`,
            sub: cats.length ? `· ${topicLabel(cats[0])}` : '',
            time: rowTime(topic),
            mark: markFor(source),
          });
          for (const mate of open ? mates : mates.slice(0, 3)) {
            out.push({ kind: 'article', topic: mate, indent: true });
          }
          if (!open && mates.length > 3) {
            out.push({
              kind: 'more',
              key: `${key}-more`,
              batch: key,
              label: `Show ${mates.length - 3} more from this batch`,
            });
          } else if (open && mates.length > 3 && !q) {
            out.push({ kind: 'more', key: `${key}-more`, batch: key, label: 'Collapse batch' });
          }
        } else {
          seen.add(topic.id);
          out.push({ kind: 'article', topic, indent: false });
        }
      }
    }
    return out;
  }, [items, openBatches, q]);

  const toggleBatch = (key: string) => {
    setOpenBatches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Always explicit, so choosing Articles sticks even when there are none yet.
  const setView = (next: ContentView) => setParams({ view: next });

  const toggleTopic = (name: string) => {
    setTopicsOn((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="ink-scroll flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="ink-canvas m-0 w-full h-full relative overflow-hidden box-border flex flex-col">
            <div className="ink-frame">
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <h1 className="ink-h1">Content</h1>
                <div className="ink-seg">
                  <button
                    type="button"
                    data-active={view === 'pages' ? 'true' : 'false'}
                    onClick={() => setView('pages')}
                  >
                    Pages{' '}
                    <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>
                      {pageCount || ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-active={view === 'articles' ? 'true' : 'false'}
                    onClick={() => setView('articles')}
                  >
                    Articles{' '}
                    <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>{counts.all}</span>
                  </button>
                  <button
                    type="button"
                    data-active={view === 'forms' ? 'true' : 'false'}
                    onClick={() => setView('forms')}
                  >
                    Forms{' '}
                    {formUnread ? (
                      <span className="fm-tab-new" title={`${formUnread} new responses`}>
                        {formUnread} new
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>
                        {formCount || ''}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    data-active={view === 'media' ? 'true' : 'false'}
                    onClick={() => setView('media')}
                  >
                    Media{' '}
                    <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>
                      {mediaCount || ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-active={view === 'design' ? 'true' : 'false'}
                    onClick={() => setView('design')}
                  >
                    Design
                  </button>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {visibility ? (
                    <Link
                      to="/site/settings#visibility"
                      className="ink-vis"
                      title="Who can see the site"
                    >
                      <span className="ink-dot" style={{ background: visibility.dot }} />
                      {visibility.label}
                    </Link>
                  ) : null}
                  {view === 'pages' || view === 'design' ? (
                    // The site's address, identity, visibility and code: Site settings.
                    <Link to="/site/settings" className="ink-btn ink-btn-quiet">
                      {view === 'pages' ? 'Page settings' : 'Site settings'}
                    </Link>
                  ) : view === 'articles' ? (
                    <Link to="/content/settings" className="ink-btn ink-btn-quiet">
                      Article settings
                    </Link>
                  ) : view === 'media' ? (
                    <Link to="/content/media/settings" className="ink-btn ink-btn-quiet">
                      Media settings
                    </Link>
                  ) : view === 'forms' ? (
                    <Link to="/content/forms/settings" className="ink-btn ink-btn-quiet">
                      Form settings
                    </Link>
                  ) : null}
                  {view === 'pages' || view === 'forms' ? (
                    <AgentNewPanel kind={view === 'forms' ? 'form' : 'page'} />
                  ) : view === 'articles' ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={!accountId}
                      onClick={() => setCreateOpen(true)}
                    >
                      New article
                    </button>
                  ) : view === 'design' ? null : (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={!accountId}
                      onClick={() => uploadMedia.current?.()}
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {view === 'pages' ? (
                <div className="ink-scroll flex-1 min-h-0 overflow-auto">
                  <CmsPagesView initial={params.get('page')} />
                </div>
              ) : view === 'forms' ? (
                <div className="ink-scroll flex-1 min-h-0 overflow-auto">
                  <CmsFormsView initial={params.get('form')} />
                </div>
              ) : view === 'design' ? (
                <div className="ink-scroll flex-1 min-h-0 overflow-auto">
                  <CmsSite mode="design" />
                </div>
              ) : view === 'media' ? (
                <MediaLibrary
                  embedded
                  onReady={(open) => {
                    uploadMedia.current = open;
                  }}
                />
              ) : (
                <>
                  <div className="ink-lib-filters">
                    <input
                      className="ink-search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search titles and summaries"
                    />
                    {filters.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className="ink-chip"
                        data-active={filter === f.id ? 'true' : 'false'}
                        onClick={() => setFilter(f.id)}
                      >
                        {f.label} <span>{counts[f.id]}</span>
                      </button>
                    ))}
                    {categories.length > 0 && <span className="ink-lib-rule" />}
                    {shownCategories.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="ink-chip"
                        data-active={topicsOn.has(name) ? 'true' : 'false'}
                        onClick={() => toggleTopic(name)}
                      >
                        {topicLabel(name)}
                      </button>
                    ))}
                    {categories.length > TOP_CATEGORIES && (
                      <button
                        type="button"
                        className="ink-chip"
                        data-active="false"
                        onClick={() => setAllCategories((v) => !v)}
                      >
                        {allCategories
                          ? 'Fewer'
                          : `${categories.length - shownCategories.length} more`}
                      </button>
                    )}
                    <span className="ink-lib-count">
                      {total.toLocaleString()} article{total === 1 ? '' : 's'} · newest first
                    </span>
                  </div>

                  {accountId && (
                    <CreateTopicModal
                      isOpen={createOpen}
                      onClose={() => setCreateOpen(false)}
                      accountId={accountId}
                      onCreated={(id) => navigate(`/content/${id}`)}
                    />
                  )}

                  <div className="ink-scroll flex-1 min-h-0 overflow-auto">
                    {loading && !page ? (
                      <Loader className="ink-muted" style={{ marginTop: 28 }} />
                    ) : topicsError && topics.length === 0 ? (
                      // A failed load is not an empty library: say so, or the
                      // person thinks their articles are gone.
                      <div className="ink-muted" style={{ padding: '48px 12px' }}>
                        <p>Your articles didn’t load. {topicsError.message}</p>
                        <button
                          type="button"
                          className="ink-btn"
                          style={{ marginTop: 12 }}
                          onClick={() => void refetchTopics()}
                        >
                          Try again
                        </button>
                      </div>
                    ) : items.length === 0 ? (
                      <p className="ink-muted" style={{ padding: '48px 12px' }}>
                        No articles match. Try a different filter or search.
                      </p>
                    ) : (
                      <div className={atomizeHeld ? undefined : 'ink-lib-nofacts'}>
                        <div className="ink-lib-head">
                          <span>Title</span>
                          <span>Topic</span>
                          <span>Status</span>
                          {atomizeHeld && <span>Facts</span>}
                          <span>Updated</span>
                        </div>
                        {rows.map((row) => {
                          if (row.kind === 'day') {
                            return (
                              <div key={row.key} className="ink-lib-day">
                                {row.label}
                              </div>
                            );
                          }
                          if (row.kind === 'batch') {
                            const open = Boolean(openBatches[row.key]) || Boolean(q);
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-batch"
                                onClick={() => toggleBatch(row.key)}
                              >
                                <span className="ink-lib-chev">{open ? '▾' : '▸'}</span>
                                <span className="ink-lib-mark">{row.mark}</span>
                                <span>{row.label}</span>
                                {row.sub ? <span className="ink-lib-sub">{row.sub}</span> : null}
                                <span className="ink-lib-time">{row.time}</span>
                              </div>
                            );
                          }
                          if (row.kind === 'more') {
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-more"
                                onClick={() => toggleBatch(row.batch)}
                              >
                                {row.label}
                              </div>
                            );
                          }
                          const topic = row.topic;
                          const bucket = libraryFilter(topic, atomizeOn);
                          const tone = STATUS_STYLE[bucket];
                          const facts = factLabel(topic);
                          const summary = decode(topic.summary || '').trim();
                          return (
                            <div
                              key={topic.id}
                              className="ink-lib-row"
                              onClick={() => navigate(`/content/${topic.id}`)}
                            >
                              <div
                                className="ink-lib-main"
                                style={{ paddingLeft: row.indent ? 22 : 0 }}
                              >
                                <div className="ink-lib-title">
                                  {decode(topic.title || 'Untitled')}
                                </div>
                                {summary ? <div className="ink-lib-summary">{summary}</div> : null}
                              </div>
                              <span className="ink-lib-topic">{topicLabel(topic.category)}</span>
                              <span className="ink-lib-status" style={{ color: tone.text }}>
                                <span className="ink-dot" style={{ background: tone.dot }} />
                                {statusLabel(topic, atomizeOn)}
                              </span>
                              {atomizeHeld && (
                                <span
                                  className="ink-lib-facts"
                                  style={{ color: facts ? 'var(--ink-ink2)' : 'var(--ink-line3)' }}
                                >
                                  {facts || '—'}
                                </span>
                              )}
                              <span className="ink-lib-time">{rowTime(topic)}</span>
                            </div>
                          );
                        })}
                        {total > PAGE_SIZE ? (
                          <div className="ink-lib-pager">
                            <span className="ink-muted">
                              {(offset + 1).toLocaleString()}–
                              {Math.min(offset + PAGE_SIZE, total).toLocaleString()} of{' '}
                              {total.toLocaleString()}
                            </span>
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={offset === 0 || loading}
                              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            >
                              Previous
                            </button>
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={offset + PAGE_SIZE >= total || loading}
                              onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                              Next
                            </button>
                          </div>
                        ) : null}
                        {offset + PAGE_SIZE >= total ? (
                          <div className="ink-list-end">
                            <div className="ink-list-end-row">
                              <span className="ink-list-end-rule" />
                              <span className="ink-list-end-gem" />
                              <span className="ink-list-end-rule" />
                            </div>
                            <p className="ink-list-end-label">The end.</p>
                            <div className="ink-list-end-note">No more articles</div>
                          </div>
                        ) : null}
                      </div>
                    )}
                    {accountId ? (
                      <RecentlyDeleted
                        variant="list"
                        accountId={accountId}
                        items={deletedArticles}
                        restoreMutation={RESTORE_TINYCMS_ARTICLE}
                        onRestored={(id) => {
                          // Deleting dropped it from the app's cache; forget that so
                          // its page loads fresh instead of waiting on a gone entry.
                          apollo.cache.evict({
                            id: 'ROOT_QUERY',
                            fieldName: 'topic',
                            args: { id },
                          });
                          apollo.cache.gc();
                          void refetchTopics();
                          void refetchDeleted();
                          navigate(`/content/${id}`);
                        }}
                        note="Restored articles come back as drafts. Publish one again to put it back on your site."
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
